import { Box, Stack, Typography } from "@mui/material"
import _ from "lodash"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { ReactComponent as NoScheme } from "../../assets/No_offers_ui.svg"
import useAppConfig from "../../hooks/useAppConfig"
import useAuth from "../../hooks/useAuth"
import Footer from "../../Layout/Footer"
import HeroBanner from "../../Layout/HeroBanner"
import ROUTE_PATHS from "../../routes/routesPath"
import IfEmpty from "../../shared/constants/IfEmpty"
import STRING_CONSTANTS from "../../shared/constants/StringConstants"
import { Loader } from "../../shared/components/UI"
import { getSchemeSubscriptions } from "../../service/scheme"
import { SchemeContainer } from "../../styles/styledComponent"
import AdditionalTextContent from "../LoyaltyInfo/AdditionalText"
import { SCHEME_STATUS, SCHEME_TABS } from "./constants"
import SchemeCard from "./SchemeCard"
import SchemePromoBanner from "./SchemePromoBanner"
import SchemeTabs from "./SchemeTabs"
import useSchemeBanners from "./useSchemeBanners"
import useSchemeBusiness from "./useSchemeBusiness"

const Scheme = () => {
  const navigate = useNavigate()
  const pwaConfig = useAppConfig()
  const { token } = useAuth()
  const business = useSchemeBusiness()
  const { bannerImg, footerImg } = useSchemeBanners(pwaConfig)
  const isAdditionalText =
    _.get(pwaConfig, "appConfig.config.additionalText.ALL_PAGES.html") ===
    "<p><br></p>"

  const [schemes, setSchemes] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(SCHEME_STATUS.ACTIVE)

  useEffect(() => {
    if (!token) return
    let cancelled = false
    setLoading(true)
    getSchemeSubscriptions({ token, business })
      .then((data) => {
        if (!cancelled) setSchemes(_.isArray(data) ? data : [])
      })
      .catch(() => {
        if (!cancelled) setSchemes([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, business])

  const counts = useMemo(
    () =>
      schemes.reduce((acc, scheme) => {
        acc[scheme.status] = (acc[scheme.status] || 0) + 1
        return acc
      }, {}),
    [schemes]
  )

  const visibleSchemes = useMemo(
    () => schemes.filter((scheme) => scheme.status === activeTab),
    [schemes, activeTab]
  )

  const onJoin = () => navigate(ROUTE_PATHS.SCHEME_JOIN)
  const onSelect = (schemeId) => navigate(`${ROUTE_PATHS.SCHEME}/${schemeId}`)

  if (loading) return <Loader />

  return (
    <SchemeContainer>
      <HeroBanner src={bannerImg} />

      <Typography component="h1" sx={{ fontSize: 22, fontWeight: 700 }}>
        {STRING_CONSTANTS.SCHEME}
      </Typography>

      <SchemePromoBanner onJoin={onJoin} />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
          {STRING_CONSTANTS.YOUR_SCHEMES}
        </Typography>
        <Typography sx={{ fontSize: 14, color: "grey.500" }}>
          ({schemes.length})
        </Typography>
      </Stack>

      <SchemeTabs
        value={activeTab}
        options={SCHEME_TABS}
        onChange={setActiveTab}
        counts={counts}
      />

      {visibleSchemes.length > 0 ? (
        <Box sx={{ display: "grid", gap: 1.5 }}>
          {visibleSchemes.map((scheme) => (
            <SchemeCard
              key={scheme.id}
              scheme={scheme}
              onSelect={onSelect}
            />
          ))}
        </Box>
      ) : (
        <IfEmpty
          Icon={NoScheme}
          head={STRING_CONSTANTS.EMPTY}
          desc={STRING_CONSTANTS.NO_SCHEMES_FOUND}
        />
      )}

      {!isAdditionalText && <AdditionalTextContent />}
      <Footer img={footerImg} />
    </SchemeContainer>
  )
}

export default Scheme
